import React, { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import classnames from 'classnames';
import { useBiLogger } from '@wix/da-bi/pkg/hooks/useBiLogger';
import TextInput from '@wix/da-ds/pkg/formControls/dsPresets/TextInput';
import SimplePopperPopover from '@wix/da-ds/pkg/popper/presets/SimplePopperPopover';
import { BiData, PageNavigationClickBiEvent } from '@wix/da-bi/pkg/events';

import s from './Dots.scss';

export interface Props {
  pageSize: number;
  totalPages: number;
  startOpen?: boolean;
  getPageUrl?: (page: number) => string;
  onChangePage?: (newPage: number) => void;
}

const Dots: React.FC<Props> = ({
  pageSize,
  totalPages,
  onChangePage,
  getPageUrl,
  startOpen = false,
}) => {
  const { t } = useTranslation();
  const logBiEvent = useBiLogger();
  const [showPopover, setShowPopover] = useState(startOpen);
  const [pageNumber, setPageNumber] = useState<number | undefined>();

  const isValidPage = !!pageNumber && pageNumber > 0 && pageNumber <= totalPages;

  const onInputChange = (ev: React.ChangeEvent<HTMLInputElement>) => {
    const value = ev.target.value;
    if (value === '' || value === undefined) {
      setPageNumber(undefined);
      return;
    }

    const number = parseInt(value, 10);
    if (!Number.isNaN(number)) {
      setPageNumber(number);
    }
  };

  const closePopover = useCallback(() => setShowPopover(false), []);

  const onKeyDown = useCallback(
    (e: React.KeyboardEvent<Element>) => {
      if (e.key === 'Enter') {
        setShowPopover(false);
        onChangePage && onChangePage(pageNumber!);
        if (getPageUrl) {
          const url = getPageUrl(pageNumber!);
          if (url) {
            logBiEvent(
              BiData<PageNavigationClickBiEvent>({
                evid: 113,
                direction: `${pageNumber}`,
                thumbs_per_page: pageSize,
              })
            );
            window.location.assign(url);
          }
        }
      }
    },
    [getPageUrl, onChangePage, pageNumber, pageSize, logBiEvent]
  );

  return (
    <SimplePopperPopover
      className={s['wrapper']}
      isVisible={showPopover}
      contentClassName={s['jump-panel']}
      showArrow={false}
      placement="bottom"
      onClickOutside={closePopover}
      popoverContent={
        <>
          <div className={s['goto']}>{t('pagination.goToPage')}</div>
          <TextInput
            id="pagination-goto-page-input"
            value={pageNumber ? `${pageNumber}` : ''}
            onChange={onInputChange}
            onKeyDown={isValidPage ? onKeyDown : undefined}
            className={s['input']}
            label={t('pagination.goToPage')}
            isLabelHidden
            autoFocus
          />
        </>
      }
    >
      <button
        className={classnames(s['dots'], showPopover && s['open'])}
        onClick={() => setShowPopover(show => !show)}
      >
        ...
      </button>
    </SimplePopperPopover>
  );
};
Dots.displayName = 'Dots';

export default Dots;
